export const SORTS = [
  "price",
  "-price",
  "term",
  "-term",
  "renewable",
  "-renewable",
  "provider",
] as const;

export const RATE_TYPES = ["fixed", "variable"] as const;
export const TERMS = ["m2m", "short", "12", "24", "36"] as const;

export type SortKey = (typeof SORTS)[number];
export type RateTypeFilter = "any" | (typeof RATE_TYPES)[number];
export type TermFilter = "any" | (typeof TERMS)[number];

export interface FinderQuery {
  zip: string | null;
  usage: number;
  sort: SortKey;
  rateType: RateTypeFilter;
  term: TermFilter;
  green100: boolean;
  hidePrepaid: boolean;
  hideTou: boolean;
  hideNew: boolean;
  verifiedOnly: boolean;
  q: string;
}

export const DEFAULT_USAGE = 1000;

const SORT_SET = new Set<string>(SORTS);
const TERM_SET = new Set<string>(TERMS);

export function defaultFinderQuery(): FinderQuery {
  return {
    zip: null,
    usage: DEFAULT_USAGE,
    sort: "price",
    rateType: "any",
    term: "any",
    green100: false,
    hidePrepaid: false,
    hideTou: false,
    hideNew: false,
    verifiedOnly: false,
    q: "",
  };
}

export function parseFinderQuery(search: string): FinderQuery {
  const params = new URLSearchParams(search);
  const query = defaultFinderQuery();

  const zip = params.get("zip");
  if (zip && /^\d{5}$/.test(zip)) query.zip = zip;

  const kwh = params.get("kwh");
  if (kwh && /^\d+$/.test(kwh)) {
    const usage = Number(kwh);
    if (usage >= 1 && usage <= 10000) query.usage = usage;
  }

  const sort = params.get("sort");
  if (sort && SORT_SET.has(sort)) query.sort = sort as SortKey;

  const type = params.get("type");
  if (type === "fixed" || type === "variable") query.rateType = type;

  const term = params.get("term");
  if (term && TERM_SET.has(term)) query.term = term as TermFilter;

  if (params.get("green") === "100") query.green100 = true;
  if (params.get("prepaid") === "0") query.hidePrepaid = true;
  if (params.get("tou") === "0") query.hideTou = true;
  if (params.get("new") === "0") query.hideNew = true;
  if (params.get("verified") === "1") query.verifiedOnly = true;

  const q = params.get("q")?.trim() ?? "";
  if (q) query.q = q;

  return query;
}

/** Query string including the leading `?`, or `""` when nothing is set. */
export function serializeFinderQuery(query: FinderQuery): string {
  const params = new URLSearchParams();
  if (query.zip) {
    params.set("zip", query.zip);
    params.set("kwh", String(query.usage));
  }
  if (query.sort !== "price") params.set("sort", query.sort);
  if (query.rateType !== "any") params.set("type", query.rateType);
  if (query.term !== "any") params.set("term", query.term);
  if (query.green100) params.set("green", "100");
  if (query.hidePrepaid) params.set("prepaid", "0");
  if (query.hideTou) params.set("tou", "0");
  if (query.hideNew) params.set("new", "0");
  if (query.verifiedOnly) params.set("verified", "1");
  const q = query.q.trim();
  if (q) params.set("q", q);
  const serialized = params.toString();
  return serialized ? `?${serialized}` : "";
}

export function filtersActive(query: FinderQuery): boolean {
  return (
    query.rateType !== "any" ||
    query.term !== "any" ||
    query.green100 ||
    query.hidePrepaid ||
    query.hideTou ||
    query.hideNew ||
    query.verifiedOnly ||
    query.q.trim() !== ""
  );
}

export function clearFilters(query: FinderQuery): FinderQuery {
  return {
    ...query,
    rateType: "any",
    term: "any",
    green100: false,
    hidePrepaid: false,
    hideTou: false,
    hideNew: false,
    verifiedOnly: false,
    q: "",
  };
}
